import { memo } from 'react';
import { getEventUrl } from 'constants/pages';
import { withEngagementSource } from 'constants/payloads/club/engagementBeacon';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { getEventLocation } from 'utils/shared/location/getEventLocation';
import FeaturedFrame from 'components/FeaturedFrame';
import Link from 'components/Link';
import MarketplaceEmptyPlaceholder from 'components/MarketplaceEmptyPlaceholder';
import classNames from 'styles/utils/classNames';
import TournamentCard from './TournamentCard';
import { TournamentGridProps, TournamentListProps, TournamentType } from './props';

const MemoizedCard = memo(TournamentCard);

function TournamentGrid({
  tournaments,
  showImageAndPeople,
  listHoveredTournamentId,
  setListHoveredTournamentId,
  impressionSource = 'BROWSE',
  wrapperClassName,
}: TournamentGridProps) {
  return (
    <div
      className={classNames(
        'mx-auto grid w-full grid-cols-1 gap-ds-xl px-4 sm:px-ds-xl md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3',
        wrapperClassName,
      )}
    >
      {tournaments.map((tournament) => (
        <div key={tournament.id} className="w-full flex-shrink-0 lg:max-w-[26rem]">
          <Link
            className="block"
            href={withEngagementSource(getEventUrl(tournament.id), impressionSource)}
          >
            <MemoizedCard
              impressionSource={impressionSource}
              placementId={tournament.placementId}
              listHoveredTournamentId={listHoveredTournamentId}
              setListHoveredTournamentId={setListHoveredTournamentId}
              tournamentId={tournament.id}
              coverImageUrl={
                tournament.coverImagePath
                  ? getImageUrl({
                      url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.coverImagePath}`,
                      path: tournament.coverImagePath,
                    })
                  : ''
              }
              organizerImageUrl={
                tournament.organizerImagePath
                  ? getImageUrl({
                      url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.organizerImagePath}`,
                      path: tournament.organizerImagePath,
                    })
                  : ''
              }
              title={tournament.title}
              registrationClosedAt={tournament.registrationClosedAt}
              startTimestamp={tournament.startTimestamp}
              status={tournament.status}
              sourceOrganizerTitle={tournament.sourceOrganizerTitle}
              sourceRegistrationCount={tournament.sourceRegistrationCount}
              registrationCount={tournament.registrationsAggregate?.aggregate?.count || 0}
              isExternal={tournament.isExternal}
              isVairRequired={tournament.isVairRequired ?? false}
              displayDate={tournament.displayDate}
              displayLocation={getEventLocation(tournament)}
              distance={tournament.distance}
              showImageAndPeople={showImageAndPeople}
            />
          </Link>
        </div>
      ))}
    </div>
  );
}

export default function TournamentList({
  bounceTournaments,
  scrapedTournaments,
  featuredTournaments,
  showImageAndPeople = true,
  listHoveredTournamentId,
  setListHoveredTournamentId,
  onClearFilters,
}: TournamentListProps) {
  const totalCount = bounceTournaments.length + scrapedTournaments.length;
  const hasBounceSection = bounceTournaments.length > 0;
  const hasScrapedSection = scrapedTournaments.length > 0;
  const hasFeaturedSection = featuredTournaments.length > 0;

  if (totalCount === 0 && !hasFeaturedSection) {
    return (
      <MarketplaceEmptyPlaceholder
        title="Hang tight! We'll have tournaments in this area soon!"
        onClearFilters={onClearFilters}
      />
    );
  }

  return (
    <div className="mx-auto flex w-full flex-col">
      {/* Featured (boosted) tournaments */}
      {hasFeaturedSection && (
        <div className="pt-ds-xl">
          <FeaturedFrame>
            <TournamentGrid
              tournaments={featuredTournaments}
              impressionSource="BOOST"
              showImageAndPeople={showImageAndPeople}
              listHoveredTournamentId={listHoveredTournamentId}
              setListHoveredTournamentId={setListHoveredTournamentId}
              wrapperClassName="!px-0 !py-2"
            />
          </FeaturedFrame>
        </div>
      )}

      {/* Bounce tournaments (top section) */}
      {hasBounceSection && (
        <div className={classNames('w-full flex-shrink-0', hasBounceSection && 'sm:pt-ds-xl')}>
          <h4 className="typography-product-card-title-desktop mb-ds-xl px-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary sm:px-8">
            Featured tournaments
          </h4>
          <TournamentGrid
            tournaments={bounceTournaments}
            showImageAndPeople={showImageAndPeople}
            listHoveredTournamentId={listHoveredTournamentId}
            setListHoveredTournamentId={setListHoveredTournamentId}
          />
        </div>
      )}

      {/* Scraped tournaments (greyed section below, like LessonList OtherCoachesSection) */}
      {hasScrapedSection && (
        <div
          className={classNames(
            'h-full bg-color-bg-lightmode-secondary py-ds-3xl dark:bg-color-bg-darkmode-secondary',
            hasBounceSection
              ? 'mt-ds-3xl border-t border-color-border-hover-lightmode dark:border-color-border-hover-darkmode'
              : 'pt-ds-xl',
          )}
        >
          <h4 className="typography-product-card-title-desktop mb-ds-xl px-4 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary sm:px-8">
            Other tournaments
          </h4>
          <TournamentGrid
            tournaments={scrapedTournaments}
            showImageAndPeople={showImageAndPeople}
            listHoveredTournamentId={listHoveredTournamentId}
            setListHoveredTournamentId={setListHoveredTournamentId}
          />
        </div>
      )}
    </div>
  );
}
